import React, { useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useFormDisable } from './form'
import { FormNewLabel } from './form-label'

// Email or URL or bare domain -> lower-cased domain, or null when it cannot be one.
// Normalisation matters more than the widget: operators paste from IdP docs
// and from email signatures, and "@" or "https://" must not become part of a
// stored domain that a security rule then compares against (spec 4.2).
export const normalizeDomain = raw => {
  let v = String(raw || '').trim().toLowerCase()
  if (!v) return null
  if (v.includes('://')) {
    try { v = new URL(v).hostname } catch (e) { return null }
  }
  if (v.includes('@')) v = v.slice(v.lastIndexOf('@') + 1)
  v = v.replace(/^@+/, '').replace(/\/.*$/, '').trim()
  if (!v.includes('.') || /\s/.test(v)) return null
  return v
}

// A list-of-strings field rendered as pills. Enter or comma commits, Backspace
// on an empty input removes the last pill, each pill has its own remove control.
// The form value is always an array of committed, normalised strings.
export const FormNewTags = ({ name, label, placeholder, normalize = v => String(v || '').trim() || null, required, disabled }) => {
  const { control } = useFormContext()
  const { field } = useController({ name, control, defaultValue: [] })
  const formDisabled = useFormDisable()
  const [draft, setDraft] = useState('')
  const [error, setError] = useState(null)
  const values = Array.isArray(field.value) ? field.value : []
  const inputId = `${name}-tags-input`

  const commit = raw => {
    const v = normalize(raw)
    if (raw.trim() && v === null) { setError(`"${raw.trim()}" is not a valid domain`); return }
    setError(null)
    setDraft('')
    if (v && !values.includes(v)) field.onChange([...values, v])
  }

  const remove = v => field.onChange(values.filter(x => x !== v))

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(draft) }
    else if (e.key === 'Backspace' && draft === '' && values.length) remove(values[values.length - 1])
  }

  const handleChange = e => {
    const v = e.target.value
    if (v.endsWith(',')) commit(v.slice(0, -1))
    else { setDraft(v); if (error) setError(null) }
  }

  return (
    <FormNewLabel name={name} label={label} required={required}>
      <div className="form-control d-flex flex-wrap align-items-center gap-1" style={{ minHeight: 38 }}>
        {values.map(v =>
          <span key={v} className="badge rounded-pill text-bg-secondary d-inline-flex align-items-center gap-1">
            {v}
            <button type="button" className="btn-close btn-close-white" style={{ fontSize: '.55rem' }}
              aria-label={`remove ${v}`} onClick={() => remove(v)} disabled={formDisabled || disabled} />
          </span>)}
        <input id={inputId} aria-label={label || name} className="border-0 flex-fill bg-transparent" style={{ outline: 'none', minWidth: 120 }}
          value={draft} placeholder={placeholder || (values.length ? '' : 'add a domain and press Enter')}
          onChange={handleChange} onKeyDown={handleKeyDown} onBlur={() => draft && commit(draft)}
          disabled={formDisabled || disabled} />
      </div>
      {error && <small className="text-danger">{error}</small>}
    </FormNewLabel>
  )
}
